import type { Broadcast, BroadcastAudience, BroadcastPreview, BroadcastRecipient, UpsertBroadcast } from '@dnx/contracts';
import { BROADCAST_FOOTER, LEAD_SOURCES, LEAD_STAGES } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Dialog, EmptyState, Field, Input, PageHeader, Select, Spinner, cn } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Megaphone, Pause, Play, Plus, Send, ShieldAlert, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { SOURCE_LABELS, STAGE_LABELS } from '../lib/labels.js';
import { meQuery } from '../lib/session.js';
import { tagsQuery } from '../reception/queries.js';

const listQuery = queryOptions({ queryKey: ['broadcasts'], queryFn: () => api<Broadcast[]>('/broadcasts'), refetchInterval: 10_000 });
const oneQuery = (id: string) => queryOptions({
  queryKey: ['broadcasts', id], queryFn: () => api<Broadcast>(`/broadcasts/${id}`),
  refetchInterval: (q) => (q.state.data?.status === 'sending' ? 5_000 : false),
});
const recipientsQuery = (id: string, live: boolean) => queryOptions({
  queryKey: ['broadcasts', id, 'recipients'], queryFn: () => api<BroadcastRecipient[]>(`/broadcasts/${id}/recipients`), refetchInterval: live ? 8_000 : false,
});
const channelsQuery = queryOptions({ queryKey: ['broadcasts', 'channels'], queryFn: () => api<{ id: string; displayName: string; status: string; provider: string }[]>('/broadcasts/channels') });

const STATUS: Record<Broadcast['status'], [string, 'neutral' | 'primary' | 'warning' | 'success' | 'danger']> = {
  draft: ['Rascunho', 'neutral'], sending: ['Enviando', 'primary'], paused: ['Pausado', 'warning'], done: ['Concluído', 'success'], cancelled: ['Cancelado', 'neutral'],
};
const when = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);

function Chips<T extends string>({ all, value, onChange, label }: { all: readonly T[]; value: T[] | undefined; onChange: (v: T[]) => void; label: (v: T) => string }) {
  const set = new Set(value ?? []);
  return (
    <div className="flex flex-wrap gap-1.5">
      {all.map((v) => (
        <button key={v} type="button" aria-pressed={set.has(v)} onClick={() => { const n = new Set(set); if (n.has(v)) n.delete(v); else n.add(v); onChange([...n]); }}
          className={cn('h-8 rounded-full border px-3 text-[13px]', set.has(v) ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong text-fg-muted hover:text-fg')}>
          {label(v)}
        </button>
      ))}
    </div>
  );
}

function Progress({ b }: { b: Broadcast }) {
  const done = b.sent + b.failed + b.skipped;
  return (
    <div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-muted"><div className="h-full rounded-full bg-primary" style={{ width: `${pct(done, b.total)}%` }} /></div>
      <p className="mt-1.5 text-[12.5px] text-fg-muted">{done} de {b.total} processados · {b.sent} enviados{b.skipped ? ` · ${b.skipped} pulados` : ''}{b.failed ? ` · ${b.failed} com falha` : ''}</p>
    </div>
  );
}

/** Disparos: campanhas para um público (etiqueta, etapa, origem), em ritmo seguro para o número. */
export function BroadcastsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery(listQuery);
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Disparos" description="Mensagem para muitos contatos de uma vez: campanhas, avisos e reativação. Sai devagar, em horário comercial, e respeita quem pediu para sair."
        actions={<Button onClick={() => void navigate({ to: '/disparos/$id', params: { id: 'novo' } })}><Plus className="size-4" />Novo disparo</Button>} />
      {isLoading ? <Spinner className="mt-10 size-5 text-fg-subtle" label="Carregando" /> : data?.length ? (
        <div className="mt-8 grid gap-3">
          {data.map((b) => (
            <Link key={b.id} to="/disparos/$id" params={{ id: b.id }}>
              <Card className="grid gap-3 p-5 transition-colors hover:border-border-strong sm:grid-cols-[1fr_auto] sm:items-center">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-semibold text-fg">{b.name}<Badge tone={STATUS[b.status][1]}>{STATUS[b.status][0]}</Badge></p>
                  <p className="mt-0.5 truncate text-[13px] text-fg-muted">{b.channelName} · {b.status === 'draft' ? `criado ${when(b.createdAt)}` : b.startedAt ? `iniciado ${when(b.startedAt)}` : ''}</p>
                  {b.status !== 'draft' && <div className="mt-3 max-w-xl"><Progress b={b} /></div>}
                </div>
                {b.status !== 'draft' && (
                  <div className="flex gap-4 text-center text-[12px] text-fg-muted">
                    <span><strong className="block text-lg text-fg">{b.delivered}</strong>entregues</span>
                    <span><strong className="block text-lg text-fg">{b.read}</strong>lidos</span>
                    <span><strong className="block text-lg text-fg">{b.replied}</strong>responderam</span>
                  </div>
                )}
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState className="mt-8" icon={<Megaphone className="size-6" />} title="Nenhum disparo ainda">
          <p>Escolha o público por etiqueta, etapa ou origem, escreva a mensagem e veja quantas pessoas vão receber antes de enviar.</p>
        </EmptyState>
      )}
    </div>
  );
}

const EMPTY = (channelAccountId: string): UpsertBroadcast => ({ name: '', channelAccountId, text: '', audience: {}, intervalSeconds: 20, dailyLimit: 300 });

function Editor({ initial, id }: { initial: UpsertBroadcast; id: string | null }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: tags = [] } = useQuery(tagsQuery);
  const { data: channels = [] } = useQuery(channelsQuery);
  const { data: me } = useSuspenseQuery(meQuery);
  const [form, setForm] = useState(initial);
  const [confirm, setConfirm] = useState(false);
  const set = <K extends keyof UpsertBroadcast>(k: K, v: UpsertBroadcast[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setAud = (a: BroadcastAudience) => set('audience', a);
  const [preview, setPreview] = useState<BroadcastPreview | null>(null);
  useEffect(() => {
    const t = setTimeout(() => { void api<BroadcastPreview>('/broadcasts/preview', { method: 'POST', body: form.audience }).then(setPreview).catch(() => setPreview(null)); }, 350);
    return () => clearTimeout(t);
  }, [form.audience]);

  const save = useMutation({
    mutationFn: () => api<Broadcast>(id ? `/broadcasts/${id}` : '/broadcasts', { method: id ? 'PATCH' : 'POST', body: form }),
    onSuccess: (b) => { void qc.invalidateQueries({ queryKey: ['broadcasts'] }); if (!id) void navigate({ to: '/disparos/$id', params: { id: b.id }, replace: true }); },
  });
  const start = useMutation({
    mutationFn: async () => { const b = await save.mutateAsync(); return api<Broadcast>(`/broadcasts/${b.id}/start`, { method: 'POST' }); },
    onSuccess: (b) => { setConfirm(false); void qc.invalidateQueries({ queryKey: ['broadcasts'] }); void navigate({ to: '/disparos/$id', params: { id: b.id }, replace: true }); },
  });
  const remove = useMutation({ mutationFn: () => api<void>(`/broadcasts/${id}`, { method: 'DELETE' }), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['broadcasts'] }); void navigate({ to: '/disparos' }); } });
  const clinic = me.organizations.find((o) => o.organizationId === me.activeOrganizationId)?.tradeName ?? '';
  const sample = form.text.replace(/\{nome\}/g, preview?.sample[0]?.split(' ')[0] ?? 'Ana').replace(/\{clinica\}|\{atendente\}/g, clinic);
  const valid = form.name.trim().length >= 2 && form.text.trim() && form.channelAccountId;
  const hours = (form.intervalSeconds * (preview?.recipients ?? 0)) / 3600;
  const days = preview?.recipients ? Math.ceil(preview.recipients / form.dailyLimit) : 0;

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="grid gap-5">
        <Card className="grid gap-5 p-5">
          <Field label="Nome do disparo" hint="Só a equipe vê."><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex.: Reativação de outubro" maxLength={80} /></Field>
          <Field label="Número que envia">
            <Select value={form.channelAccountId} onChange={(e) => set('channelAccountId', e.target.value)}>
              <option value="">Escolha…</option>
              {channels.map((c) => <option key={c.id} value={c.id} disabled={c.status !== 'connected'}>{c.displayName}{c.status !== 'connected' ? ' (desconectado)' : ''}</option>)}
            </Select>
          </Field>
          <Field label="Mensagem" hint={`O rodapé “${BROADCAST_FOOTER}” é incluído automaticamente.`}>
            <textarea value={form.text} onChange={(e) => set('text', e.target.value)} rows={5} maxLength={3900}
              placeholder="Oi, {nome}! A {clinica} abriu horários de avaliação para outubro…"
              className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-primary" />
          </Field>
        </Card>
        <Card>
          <CardHeader title="Público" description="Contatos que atendem a TODOS os filtros escolhidos. Sem filtro = todos os contatos com telefone." />
          <div className="grid gap-4 p-5">
            {tags.length > 0 && <Field label="Com alguma destas etiquetas"><Chips all={tags.map((t) => t.id)} value={form.audience.tagIds} label={(v) => tags.find((t) => t.id === v)!.name} onChange={(v) => setAud({ ...form.audience, tagIds: v })} /></Field>}
            <Field label="Lead em alguma destas etapas"><Chips all={LEAD_STAGES} value={form.audience.stages} label={(v) => STAGE_LABELS[v]!} onChange={(v) => setAud({ ...form.audience, stages: v })} /></Field>
            <Field label="Origem"><Chips all={LEAD_SOURCES} value={form.audience.sources} label={(v) => SOURCE_LABELS[v]!} onChange={(v) => setAud({ ...form.audience, sources: v })} /></Field>
          </div>
        </Card>
        <Card className="grid gap-4 p-5 sm:grid-cols-2">
          <Field label="Intervalo entre mensagens" hint="Mais lento = menos risco de bloqueio.">
            <Select value={form.intervalSeconds} onChange={(e) => set('intervalSeconds', Number(e.target.value))}>
              {[15, 20, 30, 60, 120].map((s) => <option key={s} value={s}>{s < 60 ? `${s} segundos` : `${s / 60} minuto${s > 60 ? 's' : ''}`}</option>)}
            </Select>
          </Field>
          <Field label="Limite por dia"><Input type="number" min={1} max={2000} value={form.dailyLimit} onChange={(e) => set('dailyLimit', Math.max(1, Math.min(2000, Number(e.target.value) || 1)))} /></Field>
        </Card>
      </div>

      <div className="grid content-start gap-4">
        <Card className="p-5">
          <p className="text-[11px] font-semibold tracking-[0.2em] text-fg-subtle uppercase">Quem recebe</p>
          <p className="mt-2 text-4xl font-semibold tracking-tight text-fg tabular-nums">{preview ? preview.recipients : '—'}</p>
          <p className="mt-1 text-[13px] text-fg-muted">
            {preview ? <>
              {preview.optedOut > 0 && `${preview.optedOut} pediram para sair e ficam de fora. `}
              {preview.withoutPhone > 0 && `${preview.withoutPhone} sem telefone. `}
              {preview.recipients > 0 && `Leva cerca de ${hours < 1 ? `${Math.max(1, Math.round(hours * 60))} min` : `${hours.toFixed(1).replace('.', ',')} h`} de envio${days > 1 ? `, em ${days} dias` : ''}.`}
            </> : 'Calculando…'}
          </p>
          {!!preview?.sample.length && <p className="mt-3 text-[12.5px] text-fg-subtle">Ex.: {preview.sample.join(', ')}</p>}
        </Card>
        <Card className="p-4">
          <p className="mb-2 text-[11px] font-semibold tracking-[0.2em] text-fg-subtle uppercase">Como chega</p>
          <div className="rounded-xl bg-[#e7f7e0] p-3 text-[13px] whitespace-pre-line text-[#111] dark:bg-[#0b3d2c] dark:text-[#e9f5ee]">
            {sample || 'Sua mensagem aparece aqui.'}{'\n\n'}<span className="opacity-70">{BROADCAST_FOOTER}</span>
          </div>
        </Card>
        <Alert tone="warning" title="Cuidado com o número">
          <span className="flex items-start gap-1.5">
            <ShieldAlert className="mt-0.5 size-4 shrink-0" />
            Envie só para quem conhece a clínica e aceitou receber mensagens (LGPD). Muitas denúncias de spam podem bloquear o WhatsApp da clínica.
          </span>
        </Alert>
        {(save.error ?? start.error) && <Alert tone="danger">{(save.error ?? start.error)!.message}</Alert>}
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" loading={save.isPending && !start.isPending} disabled={!valid} onClick={() => save.mutate()}>Salvar rascunho</Button>
          <Button disabled={!valid || !preview?.recipients} onClick={() => setConfirm(true)}><Send className="size-4" />Iniciar disparo</Button>
          {id && <Button variant="ghost" className="text-danger" loading={remove.isPending} onClick={() => remove.mutate()}>Apagar rascunho</Button>}
        </div>
      </div>
      {confirm && (
        <Dialog open onClose={() => setConfirm(false)} title="Iniciar o disparo?" description={`${preview?.recipients ?? 0} pessoas vão receber a mensagem pelo número “${channels.find((c) => c.id === form.channelAccountId)?.displayName ?? ''}”.`}>
          <div className="grid gap-4 p-5 text-sm text-fg-muted">
            <p>O envio começa agora (entre 8h e 20h), uma mensagem a cada ~{form.intervalSeconds} s, até {form.dailyLimit} por dia. Dá para pausar a qualquer momento.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirm(false)}>Voltar</Button>
              <Button loading={start.isPending} onClick={() => start.mutate()}><Send className="size-4" />Iniciar agora</Button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function Report({ b }: { b: Broadcast }) {
  const qc = useQueryClient();
  const { data: recipients } = useQuery(recipientsQuery(b.id, b.status === 'sending'));
  const act = useMutation({
    mutationFn: (a: 'pause' | 'resume' | 'cancel') => api<Broadcast>(`/broadcasts/${b.id}/${a}`, { method: 'POST' }),
    onSuccess: (res) => { qc.setQueryData(oneQuery(b.id).queryKey, res); void qc.invalidateQueries({ queryKey: ['broadcasts'] }); },
  });
  const next = b.nextSendAt ? new Date(b.nextSendAt) : null;
  const stat = (label: string, n: number, total?: number) => (
    <div className="rounded-xl border border-border p-4">
      <p className="text-[11px] font-semibold tracking-[0.18em] text-fg-subtle uppercase">{label}</p>
      <p className="mt-1.5 text-3xl font-semibold tracking-tight text-fg tabular-nums">{n}</p>
      {total !== undefined && <p className="text-[12px] text-fg-muted">{pct(n, total)}% dos enviados</p>}
    </div>
  );
  const DELIVERY = { queued: 'Na fila', sent: 'Enviada', delivered: 'Entregue', read: 'Lida', failed: 'Falhou' } as const;
  return (
    <div className="grid gap-5">
      <Card className="grid gap-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-sm text-fg"><Badge tone={STATUS[b.status][1]}>{STATUS[b.status][0]}</Badge>
            {b.status === 'sending' && next && <span className="text-fg-muted">{next.getTime() - Date.now() > 3_600_000 ? `retoma ${when(b.nextSendAt!)}` : 'enviando agora'}</span>}
            {b.finishedAt && <span className="text-fg-muted">terminou {when(b.finishedAt)}</span>}
          </p>
          <div className="flex gap-2">
            {b.status === 'sending' && <Button size="sm" variant="secondary" loading={act.isPending} onClick={() => act.mutate('pause')}><Pause className="size-4" />Pausar</Button>}
            {b.status === 'paused' && <Button size="sm" loading={act.isPending} onClick={() => act.mutate('resume')}><Play className="size-4" />Retomar</Button>}
            {(b.status === 'sending' || b.status === 'paused') && <Button size="sm" variant="ghost" className="text-danger" onClick={() => act.mutate('cancel')}><X className="size-4" />Cancelar</Button>}
          </div>
        </div>
        <Progress b={b} />
        {act.error && <Alert tone="danger">{act.error.message}</Alert>}
      </Card>
      <div className="grid gap-3 sm:grid-cols-4">
        {stat('Enviados', b.sent)}{stat('Entregues', b.delivered, b.sent)}{stat('Lidos', b.read, b.sent)}{stat('Responderam', b.replied, b.sent)}
      </div>
      <Card>
        <CardHeader title="Mensagem" />
        <p className="px-5 pt-3 pb-5 text-sm whitespace-pre-line text-fg">{b.text}{'\n\n'}<span className="text-fg-muted">{BROADCAST_FOOTER}</span></p>
      </Card>
      <Card className="overflow-hidden">
        <CardHeader title="Destinatários" description={`${b.total} pessoas no público congelado ao iniciar.`} />
        <ul className="mt-2 max-h-[520px] divide-y divide-border overflow-y-auto">
          {recipients?.map((r) => (
            <li key={r.contactId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-2.5 text-sm">
              <span className="min-w-[160px] flex-1 text-fg">{r.name}</span>
              {r.status === 'skipped' ? <Badge>Pulado · {r.error}</Badge>
                : r.status === 'failed' ? <Badge tone="danger">{r.error ?? 'Falhou'}</Badge>
                : r.status === 'queued' ? <span className="text-[12.5px] text-fg-subtle">na fila</span>
                : <span className="flex items-center gap-2 text-[12.5px] text-fg-muted">{DELIVERY[r.delivery ?? 'sent']}{r.replied && <Badge tone="success">respondeu</Badge>}</span>}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

export function BroadcastPage({ id }: { id: string }) {
  const isNew = id === 'novo';
  const { data: b } = useQuery({ ...oneQuery(id), enabled: !isNew });
  const { data: channels } = useQuery(channelsQuery);
  const title = isNew ? 'Novo disparo' : b?.name ?? 'Disparo';
  return (
    <div className="mx-auto max-w-6xl">
      <Link to="/disparos" className="-ml-2 inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm text-fg-muted hover:text-fg"><ArrowLeft className="size-4" />Disparos</Link>
      <PageHeader className="mt-2" title={title} />
      <div className="mt-6">
        {isNew ? (channels ? <Editor initial={EMPTY(channels.find((c) => c.status === 'connected')?.id ?? '')} id={null} /> : <Spinner className="size-5 text-fg-subtle" label="Carregando" />)
          : !b ? <Spinner className="size-5 text-fg-subtle" label="Carregando" />
          : b.status === 'draft' ? <Editor key={b.id} id={b.id} initial={{ name: b.name, channelAccountId: b.channelAccountId, text: b.text, audience: b.audience, intervalSeconds: b.intervalSeconds, dailyLimit: b.dailyLimit }} />
          : <Report b={b} />}
      </div>
    </div>
  );
}
